import React, {
  useCallback, useContext, useEffect, useState, useRef,
} from 'react';
import classNames from 'classnames';

import {
  useMapStateStore, useMapDataStore, useConfigStore, useWindowStore, useMapInteractionStore,
} from '../../../stores';
import SiteSeeContext from '../../SiteSeeGuides/SiteSeeContext';
import { getPulseTarget, PULSE_TARGETS } from '../../SiteSeeGuides/SiteSeeGuideData';

import OnImageDropdown from '../../OnImageDropdown/OnImageDropdown';
import SiteSeeTipTrigger from '../../SiteSeeTipTrigger/SiteSeeTipTrigger';
import { ReactComponent as UpDownCarets } from '../../../SVGAssets/up-down-carets.svg';

import './MapLevelsNav.scss';

const MapLevelsNav = () => {
  const [options, setOptions] = useState(null);
  const [externalActiveOptionId, setExternalActiveOptionId] = useState(null);
  const [externalToggleActive, setExternalToggleActive] = useState(false);

  const isMountedRef = useRef(true);

  // Zustand stores
  const isMobile = useWindowStore((state) => state.isMobile);
  const activeLevelSvgId = useMapStateStore((state) => state.activeLevelSvgId);
  const activeZoom = useMapStateStore((state) => state.activeZoom);
  const activeMapType = useMapStateStore((state) => state.activeMapType);
  const POIVisible = useMapStateStore((state) => state.POIVisible);
  const setActiveLevelSvgId = useMapStateStore((state) => state.setActiveLevelSvgId);
  const levelsData = useMapDataStore((state) => state.levelsData);
  const mapConfig = useConfigStore((state) => state.mapConfig);

  const { googleMapsConfig } = mapConfig || {};
  const { enableLevels: mapZoomToEnableLevels } = googleMapsConfig?.zooms || {};

  // Map interaction store
  const levelsControlVisible = useMapInteractionStore((state) => state.levelsControlVisible);
  const setLevelsControlVisible = useMapInteractionStore((state) => state.setLevelsControlVisible);

  // SiteSee context for guide state
  const siteSeeContext = useContext(SiteSeeContext);
  const { siteSeeGuideId, siteSeeGuideMode } = siteSeeContext || {};

  // Determine if levels should pulse based on active guide
  const isCoached = siteSeeGuideMode === 'tour';
  const pulseTarget = getPulseTarget(siteSeeGuideId, isMobile);
  const isPulsing = isCoached && pulseTarget === PULSE_TARGETS.LEVELS;

  // Cleanup mounted ref on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Is levels control visible?
  useEffect(() => {
    if (!isMountedRef.current) return;

    if (
      mapZoomToEnableLevels
      && activeZoom
      && activeMapType === 'roadmap'
      && !POIVisible
    ) {
      const rawLevelsControlVisible = activeZoom >= mapZoomToEnableLevels;
      if (isMountedRef.current) {
        setLevelsControlVisible(rawLevelsControlVisible);
      }
    } else if (isMountedRef.current) {
      setLevelsControlVisible(false);
    }
  }, [mapZoomToEnableLevels, activeZoom, activeMapType, POIVisible, setLevelsControlVisible]);

  // loop through levels to get btn and additional info
  useEffect(() => {
    if (!isMountedRef.current) return;

    if (levelsData) {
      const rawOptions = [];
      levelsData.forEach(
        (level, index) => {
          rawOptions[index] = {
            id: level.uuid,
            tid: level.tid,
            text: level.name,
            isDefault: level.isInitial,
            svgLevelId: level.svgLevelId,
            className: isMobile ? 'onImageDropdown__option--radio' : '',
          };
        },
      );
      if (isMountedRef.current) {
        setOptions(rawOptions);
      }
    }
  }, [isMobile, levelsData]);

  useEffect(() => {
    if (activeLevelSvgId && options?.length > 0) {
      const rawExternalActiveOption = options.find(
        (option) => option.svgLevelId === activeLevelSvgId,
      );
      if (rawExternalActiveOption) {
        setExternalActiveOptionId(rawExternalActiveOption.id);
      }
    }
  }, [activeLevelSvgId, options]);

  // handle option click
  const handleOptionOnClick = useCallback((option) => {
    if (!isMountedRef.current) return;
    setActiveLevelSvgId(option.svgLevelId);
  }, [setActiveLevelSvgId]);

  // return (levelsControlVisible || isMobile) && (
  return (
    <>

      {isMobile && (
        <button
          type="button"
          id="mapLevelsNavToggle"
          className={classNames('mobileMapControl', {
            'mobileMapControl--disabled': !levelsControlVisible,
            'mobileMapControl--pulsing': isPulsing,
          })}
          onClick={(e) => {
            e.currentTarget.blur();
            setExternalToggleActive(!externalToggleActive);
          }}
        >
          <div className="sr-only">Change Level</div>
          <UpDownCarets className="mobileMapControl__svg" />
        </button>
      )}

      <div
        className={classNames({
          mapLevelsNav__wrapper: !isMobile,
          'mapLevelsNav__wrapper--hide': !isMobile && !levelsControlVisible,
          'mapLevelsNav__wrapper--pulsing': !isMobile && isPulsing,
        })}
      >

        <OnImageDropdown
          dropdownId="mapLevelsNav"
          className={classNames({
            mapLevelsNav: !isMobile,
            mobileMapLevelsNav: isMobile,
          })}
          closeDropdownOnClick
          options={options}
          handleOptionOnClick={(option) => handleOptionOnClick(option)}
          externalActiveOptionId={externalActiveOptionId}
          useExternalToggle={isMobile}
          externalToggleActive={externalToggleActive}
          setExternalToggleActive={setExternalToggleActive}
          anchorDropdownToArrow
        />

        <SiteSeeTipTrigger siteSeeTipId="levels" />

      </div>
    </>
  );
};

export default MapLevelsNav;
